import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-semibold transition-colors outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-blue-600 text-white shadow-sm shadow-black/5 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500",
        primary: "bg-blue-600 text-white shadow-sm shadow-black/5 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500",
        destructive: "bg-red-600 text-white shadow-sm shadow-black/5 hover:bg-red-700 focus:ring-2 focus:ring-red-500",
        danger: "bg-red-600 text-white shadow-sm shadow-black/5 hover:bg-red-700 focus:ring-2 focus:ring-red-500",
        outline: "border-2 border-slate-300 bg-background shadow-sm shadow-black/5 hover:bg-slate-50 text-slate-700 focus:ring-2 focus:ring-slate-500",
        secondary: "bg-slate-100 text-slate-700 shadow-sm shadow-black/5 hover:bg-slate-200 focus:ring-2 focus:ring-slate-500",
        success: "bg-emerald-600 text-white shadow-sm shadow-black/5 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500",
        ghost: "hover:bg-slate-100 hover:text-slate-900",
        link: "text-blue-600 underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 px-3 py-1.5 text-xs rounded-lg",
        md: "h-9 px-4 py-2",
        lg: "h-10 px-6 py-3 text-base rounded-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, isLoading = false, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || isLoading}
        aria-busy={isLoading}
        {...props}
      >
        {isLoading && (
          <Loader2
            className="-ms-1 me-2 animate-spin"
            size={16}
            strokeWidth={2}
            aria-hidden="true"
          />
        )}
        {children}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
